import React, { useState } from 'react';
import { recipes, companyInfo } from '../mock';
import { Clock, ChefHat, ArrowRight, Users, Gauge, X, CheckCircle } from 'lucide-react';

const Recipes = () => {
  const [activeCategory, setActiveCategory] = useState('Tümü');
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  const categories = ['Tümü', 'Kahvaltı', 'Çorba', 'Ana Yemek', 'Atıştırmalık', 'Tatlı'];

  const filteredRecipes = activeCategory === 'Tümü' 
    ? recipes 
    : recipes.filter(recipe => recipe.category === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#216DB8] to-green-100 rounded-full mb-6">
            <ChefHat className="h-8 w-8 text-[#216DB8]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Pekala Tarifler Koleksiyonu
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Pekala ürünleriyle hazırlayabileceğiniz sağlıklı ve lezzetli tarifler
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex justify-center mb-12 overflow-x-auto pb-2">
          <div className="inline-flex bg-white rounded-full shadow-md p-2 space-x-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 whitespace-nowrap ${
                  activeCategory === category
                    ? 'bg-gradient-to-r from-[#216DB8] to-green-600 text-white shadow-lg'
                    : 'text-gray-700 hover:bg-blue-50'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Recipes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredRecipes.map((recipe) => (
            <article
              key={recipe.id}
              className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={recipe.image}
                  alt={recipe.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="inline-block text-xs font-medium text-white bg-[#216DB8] px-3 py-1 rounded-full">
                    {recipe.category}
                  </span>
                </div>
                <div className="absolute bottom-4 right-4">
                  <span className="inline-block text-xs font-medium text-white bg-green-600 px-3 py-1 rounded-full">
                    {recipe.pekaraProduct}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                  {recipe.title}
                </h2>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {recipe.description}
                </p>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                  <div className="flex items-center text-gray-600">
                    <Clock className="h-4 w-4 mr-2 text-[#216DB8]" />
                    <span>{recipe.prepTime}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Users className="h-4 w-4 mr-2 text-[#216DB8]" />
                    <span>{recipe.servings}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Gauge className="h-4 w-4 mr-2 text-[#216DB8]" />
                    <span>{recipe.difficulty}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Clock className="h-4 w-4 mr-2 text-green-600" />
                    <span>{recipe.cookTime}</span>
                  </div>
                </div>

                {/* View Recipe Button */}
                <button
                  onClick={() => setSelectedRecipe(recipe)}
                  className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-50 to-green-50 text-[#216DB8] rounded-xl font-semibold hover:from-blue-100 hover:to-green-100 transition-all duration-300 group/btn"
                >
                  <span>Tarifi Gör</span>
                  <ArrowRight className="ml-2 h-5 w-5 group-hover/btn:translate-x-1 transition-transform duration-300" />
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center bg-gradient-to-br from-emerald-50 via-white to-sky-50 rounded-3xl p-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Kendi Tarifinizi Paylaşın!
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Pekala ürünleriyle hazırladığınız özel tarifleriniz var mı? 
            Bizimle paylaşın, diğer lezzet severlerle buluşturalım!
          </p>
          <a
            href={`mailto:${companyInfo.email}?subject=Tarif Paylaşımı`}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#216DB8] to-green-600 text-white rounded-full font-bold hover:from-[#1a5a9e] hover:to-green-700 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Tarif Gönder
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </div>

      {/* Recipe Detail Modal */}
      {selectedRecipe && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn"
          onClick={() => setSelectedRecipe(null)}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10 rounded-t-3xl">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-white bg-[#216DB8] px-3 py-1 rounded-full">
                  {selectedRecipe.category}
                </span>
                <span className="text-sm font-medium text-white bg-green-600 px-3 py-1 rounded-full">
                  {selectedRecipe.pekaraProduct}
                </span>
              </div>
              <button
                onClick={() => setSelectedRecipe(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-300"
              >
                <X className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Hero Image */}
              <div className="relative rounded-2xl overflow-hidden mb-6 shadow-lg">
                <img
                  src={selectedRecipe.image}
                  alt={selectedRecipe.title}
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>

              {/* Title & Description */}
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  {selectedRecipe.title}
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {selectedRecipe.description}
                </p>
              </div>

              {/* Info Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <Clock className="h-6 w-6 text-[#216DB8] mx-auto mb-2" />
                  <p className="text-xs text-gray-600 mb-1">Hazırlık</p>
                  <p className="font-semibold text-gray-900">{selectedRecipe.prepTime}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <Clock className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <p className="text-xs text-gray-600 mb-1">Pişirme</p>
                  <p className="font-semibold text-gray-900">{selectedRecipe.cookTime}</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <Users className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                  <p className="text-xs text-gray-600 mb-1">Porsiyon</p>
                  <p className="font-semibold text-gray-900">{selectedRecipe.servings}</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <Gauge className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-xs text-gray-600 mb-1">Zorluk</p>
                  <p className="font-semibold text-gray-900">{selectedRecipe.difficulty}</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Ingredients */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <div className="w-8 h-8 bg-[#216DB8] rounded-lg flex items-center justify-center mr-3">
                      <CheckCircle className="h-5 w-5 text-white" />
                    </div>
                    Malzemeler
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-6">
                    <ul className="space-y-3">
                      {selectedRecipe.ingredients.map((ingredient, index) => (
                        <li key={index} className="flex items-start">
                          <span className="inline-block w-2 h-2 bg-[#216DB8] rounded-full mt-2 mr-3 flex-shrink-0" />
                          <span className="text-gray-700">{ingredient}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Instructions */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                      <ChefHat className="h-5 w-5 text-white" />
                    </div>
                    Yapılışı
                  </h3>
                  <div className="space-y-4">
                    {selectedRecipe.instructions.map((instruction, index) => (
                      <div key={index} className="flex items-start">
                        <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">
                          {index + 1}
                        </span>
                        <p className="text-gray-700 pt-1">{instruction}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Tips */}
              {selectedRecipe.tips && (
                <div className="mt-8 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border-l-4 border-amber-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                    💡 Şef İpucu
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{selectedRecipe.tips}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recipes;
